import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiEye,
  FiEyeOff,
  FiUser,
  FiLock,
  FiFolder,
  FiUsers,
  FiShield,
  FiMail,
  FiArrowLeft,
  FiAlertCircle,
  FiInfo,
  FiKey,
} from 'react-icons/fi';
import TopBar from './ui/TopBar';
import { API_BASE_URL, saveSession } from '../lib/session';
import { connectFirebase, withTimeout } from '../lib/firebase';

// Presentational only — restates real, existing capabilities of the
// platform (centralized records, multidisciplinary coordination,
// role-based access), not new claims.
const CAPABILITIES = [
  { label: 'Dossiers patients centralisés', icon: FiFolder },
  { label: 'Coordination pluridisciplinaire', icon: FiUsers },
  { label: 'Accès sécurisé par rôle', icon: FiShield },
];

const CODE_LENGTH = 6;
const EMPTY_CODE = Array(CODE_LENGTH).fill('');
const RESEND_COOLDOWN = 60;
const MIN_PASSWORD_LENGTH = 8;

const INPUT_CLASS =
  'w-full rounded-xl border-[1px] border-solid border-black/10 bg-white py-[12px] pl-11 text-[15px] text-ink outline-none transition placeholder:text-black/30 focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10 disabled:opacity-60';

const PRIMARY_BUTTON =
  'flex w-full items-center justify-center gap-2 rounded-full border-0 bg-brand-600 py-[14px] text-[15px] font-medium text-white transition hover:bg-brand-700 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-600/25 disabled:cursor-not-allowed disabled:opacity-60';

const FORM_CARD =
  'rounded-3xl bg-white p-8 ring-1 ring-black/[0.04] shadow-[0_2px_8px_rgba(0,0,0,0.04),0_32px_64px_-8px_rgba(0,0,0,0.18)]';

const BACK_LINK =
  'mb-6 flex items-center gap-1.5 rounded-full border-0 bg-[transparent] p-0 text-[14px] font-medium text-brand-600 hover:text-brand-700 disabled:opacity-60';

const NewPasswordInput = ({ id, label, value, onChange, disabled, autoFocus }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-black/60">
        {label}
      </label>
      <div className="relative">
        <FiLock
          size={17}
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35"
        />
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          className={`${INPUT_CLASS} pr-12`}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          className="absolute right-0.5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg border-0 bg-[transparent] text-black/35 hover:text-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
        >
          {visible ? <FiEyeOff size={18} /> : <FiEye size={18} />}
        </button>
      </div>
    </div>
  );
};

const formatDay = (value) =>
  new Date(value).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

const postAuth = async (path, body) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, data };
};

const NETWORK_ERROR = 'Impossible de joindre le serveur. Vérifiez votre connexion internet, puis réessayez.';

const Notice = ({ tone = 'error', children }) => {
  const Icon = tone === 'error' ? FiAlertCircle : FiInfo;
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2.5 rounded-2xl px-[16px] py-[12px] text-[14px] leading-snug ${
        tone === 'error' ? 'bg-red-50 text-red-700' : 'bg-brand-50 text-brand-800'
      }`}
    >
      <Icon size={17} aria-hidden="true" className="mt-px flex-none" />
      <span>{children}</span>
    </div>
  );
};

const LOGIN_STEPS = [
  'Vos identifiants personnels — ceux de votre rôle',
  'Le code CTCV-CUOMO reçu par email, valable 7 jours',
];

const LoginGuide = () => (
  <div className="rounded-2xl bg-brand-50/70 p-[16px] ring-1 ring-brand-100">
    <p className="m-0 flex items-center gap-2 text-[13px] font-semibold text-brand-800">
      <FiShield size={15} aria-hidden="true" />
      Nouveau mode de connexion
    </p>
    <ol className="mb-0 mt-2.5 list-none space-y-1.5 p-0">
      {LOGIN_STEPS.map((step, index) => (
        <li key={step} className="flex items-start gap-2 text-[13px] leading-snug text-black/65">
          <span
            aria-hidden="true"
            className="mt-px flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-brand-600 text-[11px] font-semibold text-white"
          >
            {index + 1}
          </span>
          <span>{step}</span>
        </li>
      ))}
    </ol>
    <p className="mb-0 mt-2.5 text-[12.5px] leading-snug text-black/50">
      Pas encore d’email enregistré ? Il vous sera demandé à la connexion, confirmé par un code, puis
      associé à votre compte.
    </p>
  </div>
);

const Spinner = () => (
  <span className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white/40 border-t-white" />
);

const CodeInput = ({ digits, onChange, onComplete, disabled, invalid }) => {
  const refs = useRef([]);

  const focusBox = (index) => {
    const box = refs.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))];
    if (box) box.focus();
  };

  const fill = (start, value) => {
    const next = [...digits];
    for (let k = 0; k < value.length && start + k < CODE_LENGTH; k += 1) next[start + k] = value[k];
    onChange(next);
    focusBox(start + value.length);
    if (next.every(Boolean)) onComplete(next.join(''));
  };

  const handleChange = (index, raw) => {
    const typed = raw.replace(/\D/g, '');
    if (!typed) {
      const next = [...digits];
      next[index] = '';
      onChange(next);
      return;
    }
    // Several digits at once: a paste, the phone's one-time-code autofill,
    // or a new digit typed into a box that already held one.
    fill(index, digits[index] && typed.length === 2 ? typed.slice(1) : typed);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      onChange(next);
      focusBox(index - 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (index, event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    event.preventDefault();
    fill(pasted.length >= CODE_LENGTH ? 0 : index, pasted.slice(0, CODE_LENGTH));
  };

  return (
    <div className="flex justify-between gap-2" role="group" aria-label="Code CTCV-CUOMO à 6 chiffres">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={CODE_LENGTH}
          value={digit}
          disabled={disabled}
          autoFocus={index === 0}
          aria-label={`Chiffre ${index + 1} sur ${CODE_LENGTH}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={(e) => e.target.select()}
          className={`h-14 w-full min-w-0 max-w-[56px] rounded-xl border-[1px] border-solid bg-white text-center text-[22px] font-semibold text-ink outline-none transition focus:ring-4 disabled:opacity-60 ${
            invalid
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500/10'
              : 'border-black/10 focus:border-brand-600 focus:ring-brand-600/10'
          }`}
        />
      ))}
    </div>
  );
};

const Auth = ({ login }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState('credentials');
  const [isLoading, setIsLoading] = useState(false);
  const [isSlow, setIsSlow] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [challenge, setChallenge] = useState(null);
  const [enrollEmail, setEnrollEmail] = useState('');
  const [digits, setDigits] = useState(EMPTY_CODE);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(() =>
    new URLSearchParams(location.search).get('raison') === 'expiree'
      ? 'Votre session a expiré. Reconnectez-vous pour continuer.'
      : ''
  );
  const [cooldown, setCooldown] = useState(0);
  const [forgotUsername, setForgotUsername] = useState('');
  const [resetDigits, setResetDigits] = useState(EMPTY_CODE);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changeToken, setChangeToken] = useState(null);
  const [emailRegisteredPending, setEmailRegisteredPending] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setIsSlow(false);
      return undefined;
    }
    const timer = setTimeout(() => setIsSlow(true), 5000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const completeLogin = useCallback(
    async (data) => {
      saveSession({ token: data.token, user: data.user });
      login(data.token);
      // Firestore pages need the Firebase sign-in; a slow server must not block the login.
      await withTimeout(connectFirebase().catch(() => {}), 10000);
      navigate('/role', { replace: true, state: data.emailRegistered ? { emailRegistered: true } : undefined });
    },
    [login, navigate]
  );

  const backToCredentials = (message = '', notice = '') => {
    setStep('credentials');
    setChallenge(null);
    setEnrollEmail('');
    setDigits(EMPTY_CODE);
    setResetDigits(EMPTY_CODE);
    setNewPassword('');
    setConfirmPassword('');
    setChangeToken(null);
    setError(message);
    setInfo(notice);
  };

  // A session, or the obligation to replace a password chosen by the administrator.
  const handleAuthenticated = async (data) => {
    if (data.step === 'change-password') {
      setChangeToken(data.changeToken);
      setEmailRegisteredPending(Boolean(data.emailRegistered));
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setInfo('');
      setStep('change');
      return;
    }
    await completeLogin(data);
  };

  const passwordProblem = () => {
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      return `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`;
    }
    if (newPassword !== confirmPassword) return 'Les deux mots de passe ne sont pas identiques.';
    return '';
  };

  const openForgot = () => {
    setForgotUsername(formData.username);
    setError('');
    setInfo('');
    setStep('forgot');
  };

  const requestResetCode = async (e) => {
    if (e) e.preventDefault();
    if (isLoading || (cooldown > 0 && step === 'reset')) return;
    setIsLoading(true);
    setError('');
    try {
      const { ok, data } = await postAuth('forgot-password', { username: forgotUsername });
      if (!ok) {
        setError(data.message || "La demande n'a pas pu être envoyée.");
        return;
      }
      setInfo(data.message);
      setResetDigits(EMPTY_CODE);
      setCooldown(RESEND_COOLDOWN);
      setStep('reset');
    } catch (err) {
      setError(NETWORK_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    const code = resetDigits.join('');
    const problem = code.length < CODE_LENGTH ? 'Saisissez les 6 chiffres du code reçu par email.' : passwordProblem();
    if (problem) {
      setError(problem);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const { ok, data } = await postAuth('reset-password', { username: forgotUsername, code, newPassword });
      if (ok) {
        setFormData({ username: forgotUsername, password: '' });
        backToCredentials('', data.message);
        return;
      }
      setError(data.message || "Le mot de passe n'a pas pu être modifié.");
      setResetDigits(EMPTY_CODE);
    } catch (err) {
      setError(NETWORK_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    const problem = passwordProblem();
    if (problem) {
      setError(problem);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const { ok, data } = await postAuth('change-password', { changeToken, newPassword });
      if (ok) {
        await completeLogin({ ...data, emailRegistered: data.emailRegistered || emailRegisteredPending });
        return;
      }
      if (data.restart) {
        backToCredentials(data.message);
        return;
      }
      setError(data.message || "Le mot de passe n'a pas pu être enregistré.");
    } catch (err) {
      setError(NETWORK_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const backToEnroll = () => {
    setStep('enroll');
    setDigits(EMPTY_CODE);
    setError('');
    setInfo('');
  };

  const handleCredentials = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setInfo('');
    try {
      const { ok, data } = await postAuth('login', formData);
      if (!ok) {
        setError(data.message || "La connexion a échoué. Réessayez.");
        return;
      }
      if (data.step === 'done' || data.step === 'change-password') {
        await handleAuthenticated(data);
        return;
      }
      if (data.step === 'enroll-email') {
        setChallenge({ challengeToken: data.challengeToken });
        setStep('enroll');
        return;
      }
      setChallenge(data);
      setDigits(EMPTY_CODE);
      setCooldown(data.codeSent ? RESEND_COOLDOWN : 0);
      setStep('code');
    } catch (err) {
      setError(NETWORK_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (isLoading || !challenge) return;
    setIsLoading(true);
    setError('');
    setInfo('');
    try {
      const { ok, data } = await postAuth('enroll-email', {
        challengeToken: challenge.challengeToken,
        email: enrollEmail,
      });
      if (ok) {
        setChallenge((current) => ({ ...current, ...data }));
        setDigits(EMPTY_CODE);
        setCooldown(data.codeSent ? RESEND_COOLDOWN : 0);
        setStep('code');
        return;
      }
      if (data.restart) {
        backToCredentials(data.message);
        return;
      }
      if (data.retryAfter) setCooldown(data.retryAfter);
      setError(data.message || "L'adresse n'a pas pu être enregistrée.");
    } catch (err) {
      setError(NETWORK_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (code) => {
    if (isLoading || !challenge) return;
    setIsLoading(true);
    setError('');
    try {
      const { ok, data } = await postAuth('verify-code', { challengeToken: challenge.challengeToken, code });
      if (ok) {
        await handleAuthenticated(data);
        return;
      }
      if (data.restart) {
        backToCredentials(data.message);
        return;
      }
      setError(data.message || 'Code refusé.');
      setDigits(EMPTY_CODE);
    } catch (err) {
      setError(NETWORK_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async () => {
    if (isLoading || cooldown > 0 || !challenge) return;
    setIsLoading(true);
    setError('');
    setInfo('');
    try {
      const { ok, data } = await postAuth('resend-code', { challengeToken: challenge.challengeToken });
      if (ok) {
        setChallenge((current) => ({ ...current, codeSent: true, codeExpiresAt: data.codeExpiresAt }));
        setDigits(EMPTY_CODE);
        setInfo(`Un nouveau code a été envoyé à ${data.maskedEmail}.`);
        setCooldown(RESEND_COOLDOWN);
        return;
      }
      if (data.restart) {
        backToCredentials(data.message);
        return;
      }
      if (data.retryAfter) setCooldown(data.retryAfter);
      setError(data.message || "Le code n'a pas pu être renvoyé.");
    } catch (err) {
      setError(NETWORK_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const submitCode = (e) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < CODE_LENGTH) {
      setError('Saisissez les 6 chiffres du code.');
      return;
    }
    verifyCode(code);
  };

  return (
    <div className="min-h-screen bg-mist font-sans">
      <TopBar dark />

      <div className="flex min-h-[calc(100vh-3rem)] flex-col lg:flex-row">
        {/* LEFT — dark institutional panel */}
        <div className="relative overflow-hidden bg-[#0b0c10] px-6 py-10 sm:px-10 sm:py-14 lg:flex lg:w-[45%] lg:items-center lg:px-14 lg:py-20 xl:px-20">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle,rgba(255,255,255,0.07)_1px,transparent_1.6px)] bg-[length:26px_26px]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-brand-600/30 blur-[100px] sm:h-80 sm:w-80"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-28 right-[-8%] h-72 w-72 rounded-full bg-brand-500/20 blur-[110px] sm:h-96 sm:w-96"
          />

          <div className="relative mx-auto w-full max-w-md lg:mx-0">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-400">
              Centre Hospitalier National de Fann
            </p>
            <p className="mb-0 mt-2 text-[13px] text-white/50">
              Chirurgie Thoracique et Cardiovasculaire — CTCV
            </p>

            <h1 className="mb-0 mt-[20px] text-[30px] font-bold leading-[1.08] tracking-tight text-white sm:text-4xl lg:mt-6">
              Portail de connexion
            </h1>

            <p className="mb-0 mt-[16px] max-w-sm text-[15px] leading-relaxed text-white/60">
              Accédez à votre espace pour la gestion des dossiers patients et la coordination des
              équipes du service.
            </p>

            <div className="mt-9 hidden flex-col gap-[16px] lg:flex">
              {CAPABILITIES.map(({ label, icon: Icon }) => (
                <div key={label} className="flex items-center gap-[12px]">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600/15 text-brand-400 ring-1 ring-brand-500/30">
                    <Icon size={16} aria-hidden="true" />
                  </span>
                  <span className="text-[14px] text-white/70">{label}</span>
                </div>
              ))}
            </div>

            <div className="mt-7 grid grid-cols-3 gap-[12px] lg:hidden">
              {CAPABILITIES.map(({ label, icon: Icon }) => (
                <div key={label} className="flex flex-col items-center gap-2 text-center">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600/15 text-brand-400 ring-1 ring-brand-500/30">
                    <Icon size={16} aria-hidden="true" />
                  </span>
                  <span className="text-[11px] leading-tight text-white/60">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — sign-in, in two steps */}
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 sm:py-14 lg:py-20">
          <div className="w-full max-w-[400px]">
            {step === 'credentials' ? (
              <>
                <div className="mb-6 text-center lg:text-left">
                  <h2 className="m-0 text-[22px] font-semibold tracking-tight text-ink">Connexion</h2>
                  <p className="mb-0 mt-1.5 text-[14.5px] text-black/50">
                    Connectez-vous avec votre compte personnel.
                  </p>
                </div>

                <div className="rounded-3xl bg-white p-8 ring-1 ring-black/[0.04] shadow-[0_2px_8px_rgba(0,0,0,0.04),0_32px_64px_-8px_rgba(0,0,0,0.18)]">
                  <form onSubmit={handleCredentials} className="space-y-4">
                    <LoginGuide />
                    {info && <Notice tone="info">{info}</Notice>}
                    {error && <Notice>{error}</Notice>}

                    <div>
                      <label htmlFor="username" className="mb-1.5 block text-[13px] font-medium text-black/60">
                        Nom d'utilisateur
                      </label>
                      <div className="relative">
                        <FiUser
                          size={17}
                          aria-hidden="true"
                          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35"
                        />
                        <input
                          id="username"
                          name="username"
                          type="text"
                          required
                          disabled={isLoading}
                          value={formData.username}
                          onChange={handleInputChange}
                          placeholder="ex: jdupont"
                          autoComplete="username"
                          className={`${INPUT_CLASS} pr-4`}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="mb-1.5 flex items-center justify-between gap-[12px]">
                        <label htmlFor="password" className="block text-[13px] font-medium text-black/60">
                          Mot de passe
                        </label>
                        <button
                          type="button"
                          onClick={openForgot}
                          disabled={isLoading}
                          className="rounded-md border-0 bg-[transparent] p-0 text-[13px] font-medium text-brand-600 hover:text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 disabled:opacity-60"
                        >
                          Mot de passe oublié ?
                        </button>
                      </div>
                      <div className="relative">
                        <FiLock
                          size={17}
                          aria-hidden="true"
                          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35"
                        />
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          disabled={isLoading}
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          className={`${INPUT_CLASS} pr-12`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                          className="absolute right-0.5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg border-0 bg-[transparent] text-black/35 hover:text-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
                        >
                          {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                        </button>
                      </div>
                    </div>

                    <button type="submit" disabled={isLoading} className={PRIMARY_BUTTON}>
                      {isLoading ? (
                        <>
                          <Spinner />
                          Connexion…
                        </>
                      ) : (
                        'Continuer'
                      )}
                    </button>
                    {isSlow && (
                      <p className="m-0 text-center text-[13px] text-black/45" role="status">
                        Le serveur se réveille, cela peut prendre jusqu'à une minute…
                      </p>
                    )}
                  </form>

                  <p className="mb-0 mt-[20px] flex items-start justify-center gap-1.5 text-center text-[12px] text-black/40 lg:justify-start">
                    <FiShield size={14} strokeWidth={2.25} className="mt-0.5 shrink-0 text-black/40" aria-hidden="true" />
                    <span>Accès réservé au personnel autorisé du service.</span>
                  </p>
                </div>
              </>
            ) : step === 'forgot' ? (
              <>
                <button type="button" onClick={() => backToCredentials()} disabled={isLoading} className={BACK_LINK}>
                  <FiArrowLeft size={15} aria-hidden="true" />
                  Retour à la connexion
                </button>

                <div className={FORM_CARD}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                    <FiKey size={22} aria-hidden="true" />
                  </span>
                  <h2 className="mb-0 mt-[20px] text-[22px] font-semibold tracking-tight text-ink">
                    Mot de passe oublié
                  </h2>
                  <p className="mb-0 mt-2 text-[14.5px] leading-relaxed text-black/55">
                    Saisissez votre nom d’utilisateur : nous enverrons un code à l’adresse email enregistrée
                    sur votre compte, pour que vous choisissiez un nouveau mot de passe.
                  </p>

                  <form onSubmit={requestResetCode} className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="forgot-username" className="mb-1.5 block text-[13px] font-medium text-black/60">
                        Nom d'utilisateur
                      </label>
                      <div className="relative">
                        <FiUser
                          size={17}
                          aria-hidden="true"
                          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35"
                        />
                        <input
                          id="forgot-username"
                          type="text"
                          required
                          autoFocus
                          disabled={isLoading}
                          value={forgotUsername}
                          onChange={(e) => setForgotUsername(e.target.value)}
                          placeholder="ex: jdupont"
                          autoComplete="username"
                          className={`${INPUT_CLASS} pr-4`}
                        />
                      </div>
                    </div>
                    {error && <Notice>{error}</Notice>}
                    <button type="submit" disabled={isLoading} className={PRIMARY_BUTTON}>
                      {isLoading ? (
                        <>
                          <Spinner />
                          Envoi…
                        </>
                      ) : (
                        'Recevoir un code'
                      )}
                    </button>
                  </form>

                  <p className="mb-0 mt-[20px] text-center text-[12.5px] leading-snug text-black/45">
                    Pas encore d’email enregistré sur votre compte ? Demandez à l’administrateur de
                    réinitialiser votre mot de passe.
                  </p>
                </div>
              </>
            ) : step === 'reset' ? (
              <>
                <button type="button" onClick={() => backToCredentials()} disabled={isLoading} className={BACK_LINK}>
                  <FiArrowLeft size={15} aria-hidden="true" />
                  Retour à la connexion
                </button>

                <div className={FORM_CARD}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                    <FiKey size={22} aria-hidden="true" />
                  </span>
                  <h2 className="mb-0 mt-[20px] text-[22px] font-semibold tracking-tight text-ink">
                    Nouveau mot de passe
                  </h2>

                  <form onSubmit={submitReset} className="mt-[20px] space-y-4">
                    {info && <Notice tone="info">{info}</Notice>}
                    <div>
                      <p className="mb-2 mt-0 text-[13px] font-medium text-black/60">Code reçu par email</p>
                      <CodeInput
                        digits={resetDigits}
                        onChange={(next) => {
                          setResetDigits(next);
                          if (error) setError('');
                        }}
                        onComplete={() => {}}
                        disabled={isLoading}
                        invalid={Boolean(error)}
                      />
                    </div>
                    <NewPasswordInput
                      id="reset-new-password"
                      label="Nouveau mot de passe"
                      value={newPassword}
                      onChange={setNewPassword}
                      disabled={isLoading}
                    />
                    <NewPasswordInput
                      id="reset-confirm-password"
                      label="Confirmez le mot de passe"
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                      disabled={isLoading}
                    />
                    <p className="m-0 text-[12.5px] text-black/45">{MIN_PASSWORD_LENGTH} caractères minimum.</p>
                    {error && <Notice>{error}</Notice>}
                    <button type="submit" disabled={isLoading} className={PRIMARY_BUTTON}>
                      {isLoading ? (
                        <>
                          <Spinner />
                          Enregistrement…
                        </>
                      ) : (
                        'Enregistrer le mot de passe'
                      )}
                    </button>
                  </form>

                  <div className="mt-[20px] flex justify-center">
                    <button
                      type="button"
                      onClick={() => requestResetCode()}
                      disabled={isLoading || cooldown > 0}
                      className="rounded-full border-0 bg-[transparent] px-[12px] py-2 text-[14px] font-medium text-brand-600 transition hover:bg-brand-50 disabled:cursor-not-allowed disabled:text-black/35 disabled:hover:bg-[transparent]"
                    >
                      {cooldown > 0 ? `Nouveau code possible dans ${cooldown} s` : 'Recevoir un nouveau code'}
                    </button>
                  </div>
                </div>
              </>
            ) : step === 'change' ? (
              <div className={FORM_CARD}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <FiLock size={22} aria-hidden="true" />
                </span>
                <h2 className="mb-0 mt-[20px] text-[22px] font-semibold tracking-tight text-ink">
                  Choisissez votre mot de passe
                </h2>
                <p className="mb-0 mt-2 text-[14.5px] leading-relaxed text-black/55">
                  Votre mot de passe actuel a été défini par l’administrateur. Choisissez-en un nouveau :
                  vous serez la seule personne à le connaître.
                </p>

                <form onSubmit={submitNewPassword} className="mt-6 space-y-4">
                  <NewPasswordInput
                    id="change-new-password"
                    label="Nouveau mot de passe"
                    value={newPassword}
                    onChange={setNewPassword}
                    disabled={isLoading}
                    autoFocus
                  />
                  <NewPasswordInput
                    id="change-confirm-password"
                    label="Confirmez le mot de passe"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    disabled={isLoading}
                  />
                  <p className="m-0 text-[12.5px] text-black/45">
                    {MIN_PASSWORD_LENGTH} caractères minimum, différent du mot de passe donné par l’administrateur.
                  </p>
                  {error && <Notice>{error}</Notice>}
                  <button type="submit" disabled={isLoading} className={PRIMARY_BUTTON}>
                    {isLoading ? (
                      <>
                        <Spinner />
                        Enregistrement…
                      </>
                    ) : (
                      'Enregistrer et continuer'
                    )}
                  </button>
                </form>
              </div>
            ) : step === 'enroll' ? (
              <>
                <button
                  type="button"
                  onClick={() => backToCredentials()}
                  disabled={isLoading}
                  className="mb-6 flex items-center gap-1.5 rounded-full border-0 bg-[transparent] p-0 text-[14px] font-medium text-brand-600 hover:text-brand-700 disabled:opacity-60"
                >
                  <FiArrowLeft size={15} aria-hidden="true" />
                  Changer de compte
                </button>

                <div className="rounded-3xl bg-white p-8 ring-1 ring-black/[0.04] shadow-[0_2px_8px_rgba(0,0,0,0.04),0_32px_64px_-8px_rgba(0,0,0,0.18)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                    <FiMail size={22} aria-hidden="true" />
                  </span>
                  <h2 className="mb-0 mt-[20px] text-[22px] font-semibold tracking-tight text-ink">
                    Enregistrez votre adresse email
                  </h2>
                  <p className="mb-0 mt-2 text-[14.5px] leading-relaxed text-black/55">
                    Aucune adresse email n’est encore associée à votre compte. Saisissez la vôtre : nous
                    y enverrons votre code CTCV-CUOMO, puis elle sera enregistrée pour vos prochaines
                    connexions.
                  </p>

                  <form onSubmit={handleEnroll} className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="enroll-email" className="mb-1.5 block text-[13px] font-medium text-black/60">
                        Adresse email
                      </label>
                      <div className="relative">
                        <FiMail
                          size={17}
                          aria-hidden="true"
                          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35"
                        />
                        <input
                          id="enroll-email"
                          name="email"
                          type="email"
                          required
                          autoFocus
                          disabled={isLoading}
                          value={enrollEmail}
                          onChange={(e) => {
                            setEnrollEmail(e.target.value);
                            if (error) setError('');
                          }}
                          placeholder="prenom.nom@exemple.sn"
                          autoComplete="email"
                          className={`${INPUT_CLASS} pr-4`}
                        />
                      </div>
                    </div>
                    {error && <Notice>{error}</Notice>}

                    <button type="submit" disabled={isLoading || cooldown > 0} className={PRIMARY_BUTTON}>
                      {isLoading ? (
                        <>
                          <Spinner />
                          Envoi du code…
                        </>
                      ) : cooldown > 0 ? (
                        `Nouvel envoi possible dans ${cooldown} s`
                      ) : (
                        'Recevoir mon code'
                      )}
                    </button>
                  </form>

                  <p className="mb-0 mt-[20px] text-center text-[12.5px] leading-snug text-black/45">
                    Utilisez une adresse que vous êtes seul·e à consulter. Une fois enregistrée, seul
                    l’administrateur pourra la modifier.
                  </p>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => backToCredentials()}
                  disabled={isLoading}
                  className="mb-6 flex items-center gap-1.5 rounded-full border-0 bg-[transparent] p-0 text-[14px] font-medium text-brand-600 hover:text-brand-700 disabled:opacity-60"
                >
                  <FiArrowLeft size={15} aria-hidden="true" />
                  Changer de compte
                </button>

                <div className="rounded-3xl bg-white p-8 ring-1 ring-black/[0.04] shadow-[0_2px_8px_rgba(0,0,0,0.04),0_32px_64px_-8px_rgba(0,0,0,0.18)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                    <FiMail size={22} aria-hidden="true" />
                  </span>
                  <h2 className="mb-0 mt-[20px] text-[22px] font-semibold tracking-tight text-ink">
                    {challenge.enrolling ? 'Confirmez votre adresse' : 'Code CTCV-CUOMO'}
                  </h2>
                  <p className="mb-0 mt-2 text-[14.5px] leading-relaxed text-black/55">
                    {challenge.enrolling ? (
                      <>
                        Saisissez le code CTCV-CUOMO envoyé à{' '}
                        <strong className="font-semibold text-ink">{challenge.maskedEmail}</strong>. Une
                        fois validé, cette adresse sera enregistrée sur votre compte.
                      </>
                    ) : (
                      <>
                        {challenge.codeSent
                          ? 'Votre code de la semaine vient d’être envoyé à '
                          : 'Saisissez votre code de la semaine, envoyé à '}
                        <strong className="font-semibold text-ink">{challenge.maskedEmail}</strong>.
                      </>
                    )}
                  </p>
                  <p className="mb-0 mt-1 text-[13px] text-black/40">
                    Valable jusqu’au {formatDay(challenge.codeExpiresAt)}.
                  </p>

                  <form onSubmit={submitCode} className="mt-6 space-y-4">
                    <CodeInput
                      digits={digits}
                      onChange={(next) => {
                        setDigits(next);
                        if (error) setError('');
                      }}
                      onComplete={verifyCode}
                      disabled={isLoading}
                      invalid={Boolean(error)}
                    />
                    {info && <Notice tone="info">{info}</Notice>}
                    {error && <Notice>{error}</Notice>}

                    <button type="submit" disabled={isLoading} className={PRIMARY_BUTTON}>
                      {isLoading ? (
                        <>
                          <Spinner />
                          Vérification…
                        </>
                      ) : (
                        'Valider'
                      )}
                    </button>
                  </form>

                  <div className="mt-[20px] flex flex-col items-center gap-1 text-center">
                    <button
                      type="button"
                      onClick={resendCode}
                      disabled={isLoading || cooldown > 0}
                      className="rounded-full border-0 bg-[transparent] px-[12px] py-2 text-[14px] font-medium text-brand-600 transition hover:bg-brand-50 disabled:cursor-not-allowed disabled:text-black/35 disabled:hover:bg-[transparent]"
                    >
                      {cooldown > 0 ? `Nouveau code possible dans ${cooldown} s` : 'Recevoir un nouveau code'}
                    </button>
                    {challenge.enrolling && (
                      <button
                        type="button"
                        onClick={backToEnroll}
                        disabled={isLoading}
                        className="rounded-full border-0 bg-[transparent] px-[12px] py-2 text-[14px] font-medium text-brand-600 transition hover:bg-brand-50 disabled:opacity-60"
                      >
                        Modifier l’adresse email
                      </button>
                    )}
                    <p className="m-0 text-[12.5px] text-black/40">
                      Rien reçu ? Vérifiez vos courriers indésirables.
                    </p>
                  </div>
                </div>
              </>
            )}

            <p className="mb-0 mt-8 text-center text-xs text-black/35 lg:text-left">
              © {new Date().getFullYear()} CTCV | Chirurgie Thoracique Cardiovasculaire.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
